/**
 * The seven questions asked once, after the server has been claimed and before
 * anyone else is invited in.
 *
 * Two of the steps are reports rather than questions. Storage is an operator
 * decision made in the environment, so the wizard shows what was configured and
 * what is left; the API accepts that step and discards it. The rest write server
 * policy, and are gathered here and posted in one go at the end, so an abandoned
 * wizard leaves the settings exactly as they were.
 *
 * The first channel is created through the ordinary room endpoint rather than
 * through the wizard payload. That runs the same permission check, emits the
 * same `room.created` and makes the owner its first member exactly as the
 * sidebar would — and it means the channel exists the moment it is confirmed
 * rather than conditionally on the last click.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useRequireAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import {
  useCompleteOnboarding,
  useCreateRoom,
  useOnboarding,
  useStorageStatus,
  useUpdateOnboarding,
} from '@/api/queries'
import type {
  OnboardingRequest,
  OnboardingState,
  PermissionRole,
  RegistrationMode,
  Room,
  StorageBackend,
  StorageStatus,
} from '@/api/types'
import type { SelectOption } from '@/components/ui'
import {
  Badge,
  Button,
  Checkbox,
  FullPageMessage,
  FullPageSpinner,
  IconButton,
  Select,
  TextArea,
  TextField,
  useToast,
} from '@/components/ui'
import { formatBytes } from '@/lib/format'

import { Wordmark, describeRetry, networkMessage } from './LoginPage'
import styles from './OnboardingPage.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

// ── Shape of the wizard ─────────────────────────────────────────────────────

const WIZARD_STEPS = [
  {
    id: 'organisation',
    rail: 'Organisation',
    title: 'Organisation',
    lede: 'What this server calls itself. The name is what other servers see if you ever federate, so an organisation reads better than a hostname.',
  },
  {
    id: 'access',
    rail: 'Access',
    title: 'Who may join',
    lede: 'How somebody comes to have an account on this server.',
  },
  {
    id: 'permissions',
    rail: 'Permissions',
    title: 'What people may do',
    lede: 'Each action names the lowest role allowed to take it. Administrators and the owner can always do anything below their own level.',
  },
  {
    id: 'federation',
    rail: 'Federation',
    title: 'Federation',
    lede: 'There is no open federation and no central directory: two relaii servers talk only after both administrators have approved each other by hand.',
  },
  {
    id: 'files',
    rail: 'Files',
    title: 'Files',
    lede: 'Where attachments are kept, and how much room is left for them.',
  },
  {
    id: 'messages',
    rail: 'Messages',
    title: 'Messages',
    lede: 'What people may do to what they have already sent, and how long any of it is kept.',
  },
  {
    id: 'channel',
    rail: 'First channel',
    title: 'Your first channel',
    lede: 'Somewhere for people to land. #general is the usual choice, and you can rename it or add more at any time.',
  },
] as const

const REVIEW = {
  title: 'Ready when you are',
  lede: 'This is what will be saved. Every line of it can be changed later in Administration.',
} as const

/** One past the last question: the review screen. */
const REVIEW_INDEX = WIZARD_STEPS.length

type WizardPermission = 'create_channels' | 'create_group_chats' | 'invite_users' | 'create_webhooks' | 'manage_widgets'

interface Draft {
  displayName: string
  description: string
  logoUrl: string
  registrationMode: RegistrationMode
  domains: string[]
  permissions: Record<WizardPermission, PermissionRole>
  federationEnabled: boolean
  messageEditing: boolean
  messageDeleting: boolean
  retentionDays: number | null
  channelName: string
  channelTopic: string
}

type DraftPatch = Partial<Draft>

/**
 * What the wizard proposes before it has heard from the server, and therefore
 * what "skip" accepts: invitation only, federation limited to peers approved by
 * hand, and nothing deleted on a timer.
 */
const DEFAULT_DRAFT: Draft = {
  displayName: '',
  description: '',
  logoUrl: '',
  registrationMode: 'invite_only',
  domains: [],
  permissions: {
    create_channels: 'member',
    create_group_chats: 'member',
    invite_users: 'admin',
    create_webhooks: 'admin',
    manage_widgets: 'moderator',
  },
  federationEnabled: true,
  messageEditing: true,
  messageDeleting: true,
  retentionDays: null,
  channelName: 'general',
  channelTopic: '',
}

function draftFrom(state: OnboardingState): Draft {
  const settings = state.settings
  const permissions = settings.permissions

  return {
    displayName: settings.display_name ?? '',
    description: settings.description ?? '',
    logoUrl: settings.logo_url ?? '',
    registrationMode: settings.registration_mode,
    // Copied rather than aliased: the wizard edits this list, and the query
    // cache must not see those edits before they have been saved.
    domains: [...settings.allowed_email_domains],
    permissions: {
      create_channels: permissions.create_channels,
      create_group_chats: permissions.create_group_chats,
      invite_users: permissions.invite_users,
      create_webhooks: permissions.create_webhooks,
      manage_widgets: permissions.manage_widgets ?? 'moderator',
    },
    federationEnabled: settings.federation_enabled && settings.federation_mode !== 'disabled',
    messageEditing: settings.message_editing_enabled,
    messageDeleting: settings.message_deleting_enabled,
    retentionDays: settings.retention_days,
    channelName: DEFAULT_DRAFT.channelName,
    channelTopic: DEFAULT_DRAFT.channelTopic,
  }
}

function buildPayload(draft: Draft): OnboardingRequest {
  const displayName = draft.displayName.trim()
  const description = draft.description.trim()
  const logo = draft.logoUrl.trim()
  const domains = draft.domains.map((domain) => domain.trim()).filter((domain) => domain !== '')

  return {
    organisation: {
      display_name: displayName === '' ? undefined : displayName,
      description: description === '' ? null : description,
      logo_object_key: logo === '' ? null : logo,
    },
    access: {
      registration_mode: draft.registrationMode,
      // Domains mean something only to the one mode that reads them; sending
      // them alongside another would leave a rule behind that nothing applies.
      allowed_email_domains: draft.registrationMode === 'email_domains' ? domains : [],
    },
    permissions: { permissions: draft.permissions },
    federation: {
      federation_enabled: draft.federationEnabled,
      federation_mode: draft.federationEnabled ? 'approved_peers' : 'disabled',
    },
    files: { acknowledged: true },
    messages: {
      message_editing_enabled: draft.messageEditing,
      message_deleting_enabled: draft.messageDeleting,
      retention_days: draft.retentionDays,
    },
    // No `channel` key: the first channel is created through `POST /rooms`
    // while the wizard is open, and naming it here would create a second one.
  }
}

// ── Copy ────────────────────────────────────────────────────────────────────

function humanField(field: string): string {
  const words = field.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function errorMessage(error: unknown): string {
  if (!isApiError(error)) return networkMessage()

  if (error.code === 'rate_limited') {
    return `Too many requests. Try again ${describeRetry(error.retryAfterSeconds)}.`
  }

  if (error.code === 'validation_failed') {
    const first = Object.entries(error.fieldErrors)[0]
    if (first !== undefined) {
      const message = first[1][0]
      if (message !== undefined) return `${humanField(first[0])} ${message}.`
    }
  }

  return error.message
}

const ROLE_OPTIONS: readonly SelectOption[] = [
  { value: 'member', label: 'Everyone on the server' },
  { value: 'moderator', label: 'Moderators and above' },
  { value: 'admin', label: 'Administrators and above' },
  { value: 'owner', label: 'The owner only' },
]

function toRole(value: string): PermissionRole {
  return value === 'owner' || value === 'admin' || value === 'moderator' ? value : 'member'
}

/** The label a role wears in the dropdowns, reused in the review. */
function roleLabel(role: PermissionRole): string {
  const option = ROLE_OPTIONS.find((candidate) => candidate.value === role)
  return (option?.label ?? role).toLowerCase()
}

const RETENTION_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Keep everything — nothing is deleted on a timer' },
  { value: '30', label: 'Delete messages after 30 days' },
  { value: '90', label: 'Delete messages after 90 days' },
  { value: '180', label: 'Delete messages after 180 days' },
  { value: '365', label: 'Delete messages after a year' },
]

function toRetention(value: string): number | null {
  if (value === '') return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function describeRegistration(mode: RegistrationMode): string {
  switch (mode) {
    case 'invite_only':
      return 'Invitation only'
    case 'email_domains':
      return 'Approved email domains'
    case 'open':
      return 'Open registration'
  }
}

function channelName(draft: Draft): string {
  return draft.channelName.trim().replace(/^#/, '')
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value)
}

// ── Building blocks ─────────────────────────────────────────────────────────

interface ChoiceProps {
  name: string
  value: string
  checked: boolean
  onSelect: () => void
  title: string
  children: ReactNode
}

/**
 * A radio inside its own label: the whole card is the hit target, and the
 * arrow keys still move between options because it is a real radio group.
 */
function Choice({ name, value, checked, onSelect, title, children }: ChoiceProps): ReactElement {
  return (
    <label className={cx(styles.choice, checked && styles.choiceSelected)}>
      <input
        className={styles.choiceInput}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
      />
      <span className={styles.choiceBody}>
        <span className={styles.choiceTitle}>{title}</span>
        <span className={styles.choiceText}>{children}</span>
      </span>
    </label>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  )
}

function ReviewRow({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <div className={styles.reviewRow}>
      <dt className={styles.reviewTerm}>{term}</dt>
      <dd className={styles.reviewValue}>{children}</dd>
    </div>
  )
}

function CrossIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" fill="none" aria-hidden="true" focusable="false">
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

// ── 1. Organisation ─────────────────────────────────────────────────────────

interface DraftStepProps {
  draft: Draft
  update: (patch: DraftPatch) => void
}

function OrganisationStep({
  draft,
  update,
  serverId,
}: DraftStepProps & { serverId: string }): ReactElement {
  const logo = draft.logoUrl.trim()

  return (
    <div className={styles.fields}>
      <TextField
        label="Name"
        value={draft.displayName}
        onChange={(event) => update({ displayName: event.target.value })}
        placeholder={serverId}
        hint="Shown in the header, and to any server you federate with."
      />

      <TextArea
        label="Description"
        value={draft.description}
        onChange={(event) => update({ description: event.target.value })}
        rows={3}
        hint="A line or two about who this server is for. Optional."
      />

      <TextField
        label="Logo image address"
        type="url"
        inputMode="url"
        value={draft.logoUrl}
        onChange={(event) => update({ logoUrl: event.target.value })}
        placeholder="https://example.com/logo.png"
        hint="A link to an image, over HTTP or HTTPS. To upload one from your computer instead, finish setup and go to Administration → Server."
        error={logo !== '' && !isHttpUrl(logo) ? 'Enter a full address beginning with https://' : null}
      />

      {isHttpUrl(logo) ? (
        <div className={styles.logoPreview}>
          <img className={styles.logoImage} src={logo} alt="" referrerPolicy="no-referrer" />
          <span className={styles.choiceText}>
            Preview. If nothing appears, that address is not reachable from this browser.
          </span>
        </div>
      ) : null}
    </div>
  )
}

// ── 2. Access ───────────────────────────────────────────────────────────────

interface AccessStepProps extends DraftStepProps {
  onDomainChange: (position: number, value: string) => void
  onDomainAdd: () => void
  onDomainRemove: (position: number) => void
}

function AccessStep({
  draft,
  update,
  onDomainChange,
  onDomainAdd,
  onDomainRemove,
}: AccessStepProps): ReactElement {
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Registration</legend>

      <div className={styles.choices}>
        <Choice
          name="registration"
          value="invite_only"
          checked={draft.registrationMode === 'invite_only'}
          onSelect={() => update({ registrationMode: 'invite_only' })}
          title="Invitation only"
        >
          Nobody can create an account without a link from an administrator. The safe default, and
          the right answer for almost every team.
        </Choice>

        <Choice
          name="registration"
          value="email_domains"
          checked={draft.registrationMode === 'email_domains'}
          onSelect={() => update({ registrationMode: 'email_domains' })}
          title="Approved email domains"
        >
          Anyone with an address at one of the domains you list can sign themselves up.
        </Choice>

        {draft.registrationMode === 'email_domains' ? (
          <div className={styles.choiceExtra}>
            <p className={styles.choiceText}>
              Domains only, without the <code>@</code>. Every address at a listed domain can
              register, so list only domains you control.
            </p>

            {draft.domains.length === 0 ? (
              <p className={styles.footnote}>
                No domains yet — until one is added, nobody can register this way.
              </p>
            ) : null}

            {draft.domains.map((domain, position) => (
              // Keyed by position: two rows may legitimately hold the same text
              // while one of them is being typed.
              <div className={styles.domainRow} key={position}>
                <TextField
                  className={styles.domainField}
                  label={`Domain ${position + 1}`}
                  labelHidden
                  value={domain}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="example.com"
                  onChange={(event) => onDomainChange(position, event.target.value)}
                />
                <IconButton
                  label={`Remove domain ${position + 1}`}
                  onClick={() => onDomainRemove(position)}
                >
                  <CrossIcon />
                </IconButton>
              </div>
            ))}

            <div>
              <Button size="sm" onClick={onDomainAdd}>
                Add a domain
              </Button>
            </div>
          </div>
        ) : null}

        <Choice
          name="registration"
          value="open"
          checked={draft.registrationMode === 'open'}
          onSelect={() => update({ registrationMode: 'open' })}
          title="Open registration"
        >
          Anyone who can reach this server can create an account. Only sensible on a private
          network.
        </Choice>
      </div>
    </fieldset>
  )
}

// ── 3. Permissions ──────────────────────────────────────────────────────────

interface PermissionsStepProps {
  permissions: Record<WizardPermission, PermissionRole>
  onChange: (action: WizardPermission, role: PermissionRole) => void
}

function PermissionsStep({ permissions, onChange }: PermissionsStepProps): ReactElement {
  return (
    <>
      <div className={styles.grid}>
        <Select
          label="Create channels"
          options={ROLE_OPTIONS}
          value={permissions.create_channels}
          onChange={(event) => onChange('create_channels', toRole(event.target.value))}
        />
        <Select
          label="Start group chats"
          options={ROLE_OPTIONS}
          value={permissions.create_group_chats}
          onChange={(event) => onChange('create_group_chats', toRole(event.target.value))}
        />
        <Select
          label="Invite people to the server"
          options={ROLE_OPTIONS}
          value={permissions.invite_users}
          onChange={(event) => onChange('invite_users', toRole(event.target.value))}
        />
        <Select
          label="Create webhooks"
          options={ROLE_OPTIONS}
          value={permissions.create_webhooks}
          onChange={(event) => onChange('create_webhooks', toRole(event.target.value))}
        />
        <Select label="Manage embeds and widgets" options={ROLE_OPTIONS} value={permissions.manage_widgets}
          onChange={(event) => onChange('manage_widgets', toRole(event.target.value))} />
      </div>

      <p className={styles.footnote}>
        A webhook can post under a name of its own choosing, which is why it is usually worth
        keeping to administrators.
      </p>
    </>
  )
}

// ── 4. Federation ───────────────────────────────────────────────────────────

function FederationStep({ draft, update }: DraftStepProps): ReactElement {
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Other servers</legend>

      <div className={styles.choices}>
        <Choice
          name="federation"
          value="approved_peers"
          checked={draft.federationEnabled}
          onSelect={() => update({ federationEnabled: true })}
          title="Approved peers only"
        >
          You can connect to a partner’s server later and share individual channels with it.
          Nothing happens until you set a peer up yourself.
        </Choice>

        <Choice
          name="federation"
          value="disabled"
          checked={!draft.federationEnabled}
          onSelect={() => update({ federationEnabled: false })}
          title="Disabled"
        >
          This server refuses every federation request outright. You can turn it on later if you
          ever need it.
        </Choice>
      </div>
    </fieldset>
  )
}

// ── 5. Files: a report, not a question ──────────────────────────────────────

/**
 * Whether the server scans uploads. Not part of `StorageStatus` in the client
 * API today, so it is read defensively off the same document: a server that
 * grows the field is understood without a client release, and one that has not
 * says so rather than being guessed at.
 */
function readAntivirus(status: StorageStatus): boolean | null {
  const record = status as unknown as Record<string, unknown>

  const direct = record['antivirus']
  if (typeof direct === 'boolean') return direct

  if (typeof direct === 'object' && direct !== null) {
    const enabled = (direct as Record<string, unknown>)['enabled']
    if (typeof enabled === 'boolean') return enabled
  }

  const flag = record['antivirus_enabled']
  return typeof flag === 'boolean' ? flag : null
}

function storageRemedy(backend: StorageBackend): string {
  return backend === 's3'
    ? 'Check the bucket name, endpoint, region and credentials in the server’s configuration, then restart relaii.'
    : 'Check that the storage directory exists, is writable by the relaii process, and that its volume is still mounted.'
}

function storageTone(status: StorageStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status.state) {
    case 'ok':
      return 'success'
    case 'warning':
      return 'warning'
    case 'critical':
    case 'full':
      return 'danger'
    case 'unknown':
      return 'neutral'
  }
}

interface FilesStepProps {
  status: StorageStatus
  maxUploadBytes: number | null
}

function FilesStep({ status, maxUploadBytes }: FilesStepProps): ReactElement {
  const antivirus = readAntivirus(status)
  const local = status.backend === 'local'
  const limit = status.limit_bytes
  const used = status.used_bytes
  const ratio =
    status.usage_ratio ?? (limit !== undefined && limit > 0 ? Math.min(used / limit, 1) : null)
  const marginPercent = Math.round((status.margin_ratio ?? 0.25) * 100)
  const failing = typeof status.error === 'string' && status.error !== ''

  const meterTone =
    ratio === null || ratio < 0.75
      ? undefined
      : ratio < 0.9
        ? styles.meterWarning
        : styles.meterCritical

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <p className={styles.panelTitle}>{local ? 'Local disk' : 'S3-compatible object storage'}</p>
          <Badge tone={storageTone(status)}>{status.state}</Badge>
        </div>

        {ratio === null ? null : (
          <div className={styles.meter} aria-hidden="true">
            {/*
              The one dimension a stylesheet cannot know: it comes from the
              measurement. The same numbers are spelled out underneath, which is
              why the bar itself is hidden from assistive technology.
            */}
            <div
              className={cx(styles.meterFill, meterTone)}
              style={{ width: `${Math.round(ratio * 100)}%` }}
            />
          </div>
        )}

        <dl className={styles.facts}>
          <Fact label="Used">{formatBytes(used)}</Fact>
          {limit === undefined ? null : <Fact label="Limit">{formatBytes(limit)}</Fact>}
          {status.available_bytes === undefined ? null : (
            <Fact label="Free for uploads">{formatBytes(status.available_bytes)}</Fact>
          )}
          {status.disk_total_bytes === undefined ? null : (
            <Fact label="Volume size">{formatBytes(status.disk_total_bytes)}</Fact>
          )}
          {status.disk_available_bytes === undefined ? null : (
            <Fact label="Free on the volume">{formatBytes(status.disk_available_bytes)}</Fact>
          )}
          {status.path === undefined ? null : <Fact label="Path">{status.path}</Fact>}
          <Fact label="Largest single upload">
            {maxUploadBytes === null ? 'Set by the server' : formatBytes(maxUploadBytes)}
          </Fact>
          <Fact label="Antivirus">
            {antivirus === null
              ? 'Not reported by this server'
              : antivirus
                ? 'On — files are scanned before anyone can download them'
                : 'Off'}
          </Fact>
        </dl>
      </div>

      {local ? (
        <p className={styles.note}>
          relaii keeps {marginPercent}% of the volume free rather than filling it, so a full
          uploads directory can never be what takes the database — and the server — down with it.
          {status.fixed_limit === true
            ? ' The limit above was pinned by the operator rather than computed from the volume.'
            : ' The limit above is computed from the volume, so growing the disk raises it.'}
        </p>
      ) : (
        <p className={styles.note}>
          Capacity is the bucket’s business, so relaii does not track a quota for it. The upload
          ceiling above still applies to every single file.
        </p>
      )}

      {failing ? (
        <div className={styles.problem} role="alert">
          <p>
            <strong>Storage reported a problem.</strong> {storageRemedy(status.backend)} Chat keeps
            working while storage is down — only uploads and downloads are affected.
          </p>
          <p className={styles.problemDetail}>{status.error}</p>
        </div>
      ) : null}

      <p className={styles.footnote}>
        Storage is configured in the server’s environment rather than from the browser, so there is
        nothing to change on this step — only something to check before people start sending files.
      </p>
    </>
  )
}

// ── 6. Messages ─────────────────────────────────────────────────────────────

function MessagesStep({ draft, update }: DraftStepProps): ReactElement {
  return (
    <div className={styles.fields}>
      <Checkbox
        label="Let people edit their own messages"
        checked={draft.messageEditing}
        onChange={(event) => update({ messageEditing: event.target.checked })}
        hint="An edited message is marked as edited; the change is never silent."
      />

      <Checkbox
        label="Let people delete their own messages"
        checked={draft.messageDeleting}
        onChange={(event) => update({ messageDeleting: event.target.checked })}
        hint="A deleted message leaves a placeholder, so a conversation never quietly loses a turn."
      />

      <Select
        label="Retention"
        options={RETENTION_OPTIONS}
        value={draft.retentionDays === null ? '' : String(draft.retentionDays)}
        onChange={(event) => update({ retentionDays: toRetention(event.target.value) })}
        hint="Applies to every room on this server, and deletion is permanent."
      />
    </div>
  )
}

// ── 7. First channel ────────────────────────────────────────────────────────

interface ChannelStepProps extends DraftStepProps {
  created: Room | null
  error: string | null
  pending: boolean
  onCreate: () => void
}

function ChannelStep({
  draft,
  update,
  created,
  error,
  pending,
  onCreate,
}: ChannelStepProps): ReactElement {
  if (created !== null) {
    return (
      <p className={cx(styles.notice, styles.noticeSuccess)} role="status">
        #{created.name ?? channelName(draft)} is ready, and you are its first member.
      </p>
    )
  }

  const name = channelName(draft)

  return (
    <>
      <div className={styles.fields}>
        <TextField
          label="Channel name"
          value={draft.channelName}
          onChange={(event) => update({ channelName: event.target.value })}
          leading="#"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          error={error}
        />

        <TextField
          label="Topic"
          value={draft.channelTopic}
          onChange={(event) => update({ channelTopic: event.target.value })}
          placeholder="Anything and everything"
          hint="Optional. Shown at the top of the channel."
        />
      </div>

      <div>
        <Button variant="primary" loading={pending} onClick={onCreate}>
          Create #{name === '' ? 'general' : name}
        </Button>
      </div>

      <p className={styles.footnote}>
        Skipping this is fine — the first channel can be made from the sidebar whenever you like.
      </p>
    </>
  )
}

// ── Review ──────────────────────────────────────────────────────────────────

interface ReviewStepProps {
  draft: Draft
  serverId: string
  backend: StorageBackend
  created: Room | null
}

function ReviewStep({ draft, serverId, backend, created }: ReviewStepProps): ReactElement {
  const domains = draft.domains.map((domain) => domain.trim()).filter((domain) => domain !== '')
  const name = draft.displayName.trim()

  return (
    <dl className={styles.review}>
      <ReviewRow term="Name">{name === '' ? serverId : name}</ReviewRow>

      <ReviewRow term="Registration">
        {describeRegistration(draft.registrationMode)}
        {draft.registrationMode === 'email_domains'
          ? domains.length === 0
            ? ' — no domains listed yet'
            : ` — ${domains.join(', ')}`
          : ''}
      </ReviewRow>

      <ReviewRow term="Channels">Created by {roleLabel(draft.permissions.create_channels)}</ReviewRow>
      <ReviewRow term="Group chats">
        Started by {roleLabel(draft.permissions.create_group_chats)}
      </ReviewRow>
      <ReviewRow term="Invitations">Sent by {roleLabel(draft.permissions.invite_users)}</ReviewRow>
      <ReviewRow term="Embeds and widgets">Managed by {roleLabel(draft.permissions.manage_widgets)}</ReviewRow>
      <ReviewRow term="Webhooks">Created by {roleLabel(draft.permissions.create_webhooks)}</ReviewRow>

      <ReviewRow term="Federation">
        {draft.federationEnabled ? 'Approved peers only' : 'Disabled'}
      </ReviewRow>

      <ReviewRow term="Files">
        {backend === 's3' ? 'S3-compatible object storage' : 'Local disk'}
      </ReviewRow>

      <ReviewRow term="Messages">
        {draft.messageEditing ? 'Editing on' : 'Editing off'},{' '}
        {draft.messageDeleting ? 'deleting on' : 'deleting off'},{' '}
        {draft.retentionDays === null
          ? 'kept indefinitely'
          : `kept for ${draft.retentionDays} days`}
      </ReviewRow>

      <ReviewRow term="First channel">
        {created === null ? 'None yet' : `#${created.name ?? channelName(draft)}`}
      </ReviewRow>
    </dl>
  )
}

// ── The page ────────────────────────────────────────────────────────────────

export function OnboardingPage(): ReactElement {
  const { user, loading, server, refresh } = useRequireAuth()
  const navigate = useNavigate()
  const toast = useToast()

  const onboarding = useOnboarding()
  const storage = useStorageStatus()
  const updateOnboarding = useUpdateOnboarding()
  const completeOnboarding = useCompleteOnboarding()
  const createRoom = useCreateRoom()

  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT)
  const [index, setIndex] = useState(0)
  const [furthest, setFurthest] = useState(0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [channelError, setChannelError] = useState<string | null>(null)
  const [createdChannel, setCreatedChannel] = useState<Room | null>(null)
  const [finishing, setFinishing] = useState(false)

  const headingRef = useRef<HTMLHeadingElement>(null)
  const seeded = useRef(false)
  const settled = useRef(false)

  // Seeded once. A later refetch must not overwrite answers already typed, so
  // the server's copy is the starting point and nothing more.
  useEffect(() => {
    const state = onboarding.data
    if (seeded.current || state === undefined) return

    seeded.current = true
    setDraft(draftFrom(state))
  }, [onboarding.data])

  // Moving the wizard on moves focus with it, so somebody using a screen reader
  // lands on the new question rather than staying on a button whose meaning has
  // just changed underneath them.
  useEffect(() => {
    if (!settled.current) {
      settled.current = true
      return
    }
    headingRef.current?.focus()
  }, [index])

  const goTo = useCallback((next: number): void => {
    setIndex(next)
    setFurthest((seen) => Math.max(seen, next))
  }, [])

  const update = useCallback((patch: DraftPatch): void => {
    setDraft((current) => ({ ...current, ...patch }))
  }, [])

  const setPermission = useCallback((action: WizardPermission, role: PermissionRole): void => {
    setDraft((current) => ({ ...current, permissions: { ...current.permissions, [action]: role } }))
  }, [])

  const onDomainChange = useCallback((position: number, value: string): void => {
    setDraft((current) => ({
      ...current,
      domains: current.domains.map((existing, other) => (other === position ? value : existing)),
    }))
  }, [])

  const onDomainAdd = useCallback((): void => {
    setDraft((current) => ({ ...current, domains: [...current.domains, ''] }))
  }, [])

  const onDomainRemove = useCallback((position: number): void => {
    setDraft((current) => ({
      ...current,
      domains: current.domains.filter((_existing, other) => other !== position),
    }))
  }, [])

  const onCreateChannel = useCallback((): void => {
    const name = channelName(draft)
    const topic = draft.channelTopic.trim()

    if (name === '') {
      setChannelError('Give the channel a name.')
      return
    }

    setChannelError(null)

    const run = async (): Promise<void> => {
      try {
        const room = await createRoom.mutateAsync({
          kind: 'channel',
          name,
          topic: topic === '' ? undefined : topic,
          // The first channel exists for everyone who joins the server later.
          visibility: 'public',
        })

        setCreatedChannel(room)
        toast.success(`#${room.name ?? name} is ready.`)
      } catch (caught) {
        setChannelError(errorMessage(caught))
      }
    }

    void run()
  }, [createRoom, draft, toast])

  const onFinish = useCallback(async (): Promise<void> => {
    setSubmitError(null)
    setFinishing(true)

    try {
      await updateOnboarding.mutateAsync(buildPayload(draft))
      await completeOnboarding.mutateAsync()

      // The route gate decides whether to send the owner back here by reading
      // `onboarding_completed_at` off the signed-in user, so the session has to
      // be re-read before leaving or the next navigation bounces straight back.
      await refresh()

      toast.success('Your server is set up.')
      navigate('/', { replace: true })
    } catch (caught) {
      setSubmitError(errorMessage(caught))
      setFinishing(false)
    }
  }, [completeOnboarding, draft, navigate, refresh, toast, updateOnboarding])

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault()

      if (index >= REVIEW_INDEX) {
        void onFinish()
        return
      }

      goTo(index + 1)
    },
    [goTo, index, onFinish],
  )

  if (loading || user === null) {
    return <FullPageSpinner label="Loading setup" />
  }

  if (!user.is_owner) {
    return (
      <FullPageMessage
        title="Setup is the owner's to do"
        description="This wizard writes server-wide policy, so it is offered only to the person who claimed this server. Ask them to finish it."
      />
    )
  }

  if (onboarding.isError) {
    return (
      <FullPageMessage
        title="Setup could not be loaded"
        description={errorMessage(onboarding.error)}
        actions={
          <Button variant="primary" onClick={() => void onboarding.refetch()}>
            Try again
          </Button>
        }
      />
    )
  }

  const state = onboarding.data

  if (state === undefined) {
    return <FullPageSpinner label="Loading setup" />
  }

  const current = WIZARD_STEPS[index]
  const onReview = index >= REVIEW_INDEX
  const heading = onReview ? REVIEW : (current ?? REVIEW)
  const storageStatus = storage.data ?? state.storage
  const busy = finishing || updateOnboarding.isPending || completeOnboarding.isPending

  return (
    <main className={styles.page}>
      <header className={styles.masthead}>
        <Wordmark small />
        <h1 className={styles.title}>Set up your server</h1>
        <p className={styles.lede}>
          Seven questions about how {state.settings.display_name ?? state.server_id} should behave.
          Every answer can be changed later in Administration — none of this is a one-way door.
        </p>
      </header>

      <div className={styles.shell}>
        <nav className={styles.rail} aria-label="Setup steps">
          <p className={styles.railCount}>
            {onReview ? 'Review' : `Step ${index + 1} of ${WIZARD_STEPS.length}`}
          </p>

          <ol className={styles.progress}>
            {WIZARD_STEPS.map((step, position) => {
              const isCurrent = position === index
              const done = position < index

              return (
                <li key={step.id}>
                  {position <= furthest ? (
                    <button
                      type="button"
                      className={cx(
                        styles.step,
                        isCurrent && styles.stepCurrent,
                        done && styles.stepDone,
                      )}
                      aria-current={isCurrent ? 'step' : undefined}
                      onClick={() => goTo(position)}
                    >
                      <span className={styles.stepIndex} aria-hidden="true">
                        {position + 1}
                      </span>
                      <span className={styles.stepLabel}>{step.rail}</span>
                    </button>
                  ) : (
                    <span className={styles.stepStatic}>
                      <span className={styles.stepIndex} aria-hidden="true">
                        {position + 1}
                      </span>
                      <span className={styles.stepLabel}>{step.rail}</span>
                    </span>
                  )}
                </li>
              )
            })}
          </ol>
        </nav>

        <form className={styles.card} onSubmit={onSubmit} noValidate>
          {state.completed ? (
            <p className={cx(styles.notice, styles.noticeInfo)} role="status">
              Setup has already been completed on this server. Going through it again replaces the
              settings below.
            </p>
          ) : null}

          {submitError === null ? null : (
            <p className={cx(styles.notice, styles.noticeError)} role="alert">
              {submitError}
            </p>
          )}

          <div className={styles.heading}>
            <h2 className={styles.stepTitle} ref={headingRef} tabIndex={-1}>
              {heading.title}
            </h2>
            <p className={styles.stepLede}>{heading.lede}</p>
          </div>

          {onReview ? (
            <ReviewStep
              draft={draft}
              serverId={state.server_id}
              backend={storageStatus.backend}
              created={createdChannel}
            />
          ) : null}

          {current?.id === 'organisation' ? (
            <OrganisationStep draft={draft} update={update} serverId={state.server_id} />
          ) : null}

          {current?.id === 'access' ? (
            <AccessStep
              draft={draft}
              update={update}
              onDomainChange={onDomainChange}
              onDomainAdd={onDomainAdd}
              onDomainRemove={onDomainRemove}
            />
          ) : null}

          {current?.id === 'permissions' ? (
            <PermissionsStep permissions={draft.permissions} onChange={setPermission} />
          ) : null}

          {current?.id === 'federation' ? <FederationStep draft={draft} update={update} /> : null}

          {current?.id === 'files' ? (
            <FilesStep status={storageStatus} maxUploadBytes={server?.max_upload_bytes ?? null} />
          ) : null}

          {current?.id === 'messages' ? <MessagesStep draft={draft} update={update} /> : null}

          {current?.id === 'channel' ? (
            <ChannelStep
              draft={draft}
              update={update}
              created={createdChannel}
              error={channelError}
              pending={createRoom.isPending}
              onCreate={onCreateChannel}
            />
          ) : null}

          <div className={styles.controls}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => goTo(REVIEW_INDEX)}
              disabled={onReview || busy}
            >
              Skip the rest and use these defaults
            </button>

            <div className={styles.controlsGroup}>
              <Button onClick={() => goTo(Math.max(index - 1, 0))} disabled={index === 0 || busy}>
                Back
              </Button>

              <Button type="submit" variant="primary" loading={busy}>
                {onReview ? 'Finish setup' : 'Next'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </main>
  )
}
